import React, { useState, Children } from 'react';
import { SearchIcon, ZoomInIcon, ZoomOutIcon, RotateCcwIcon, ChevronDownIcon, ChevronUpIcon, LayoutGridIcon, GitBranchIcon, MapPinIcon, MailIcon, PhoneIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
interface Person {
  id: string;
  name: string;
  title: string;
  dept: string;
  color: string;
  avatar: string;
  email: string;
  phone: string;
  location: string;
  recruitedBy?: string;
  children: Person[];
}
const orgTree: Person = {
  id: 'jw',
  name: 'Jennifer Walsh',
  title: 'CEO & Founder',
  dept: 'Executive',
  color: '#1A2B3C',
  avatar: 'JW',
  email: 'jennifer@swifthl.com',
  phone: '(602) 555-0101',
  location: 'Phoenix, AZ',
  children: [{
    id: 'rc',
    name: 'Robert Chen',
    title: 'Chief Operating Officer',
    dept: 'Executive',
    color: '#1E6FA0',
    avatar: 'RC',
    email: 'robert@swifthl.com',
    phone: '(602) 555-0102',
    location: 'Phoenix, AZ',
    children: [{
      id: 'cm',
      name: 'Carlos Mendez',
      title: 'Branch Manager',
      dept: 'Sales',
      color: '#2B8DC4',
      avatar: 'CM',
      email: 'carlos@swifthl.com',
      phone: '(602) 555-0203',
      location: 'Phoenix, AZ',
      children: [{
        id: 'sk',
        name: 'Sarah Kim',
        title: 'Sr. Loan Originator',
        dept: 'Sales',
        color: '#1A2B3C',
        avatar: 'SK',
        email: 'sarah@swifthl.com',
        phone: '(602) 555-0201',
        location: 'Phoenix, AZ',
        recruitedBy: 'Carlos Mendez',
        children: [{
          id: 'ac',
          name: 'Alex Chen',
          title: 'Loan Originator',
          dept: 'Sales',
          color: '#1A2B3C',
          avatar: 'AC',
          email: 'alex@swifthl.com',
          phone: '(602) 555-0204',
          location: 'Phoenix, AZ',
          recruitedBy: 'Sarah Kim',
          children: []
        }]
      }, {
        id: 'pr',
        name: 'Priya Rajan',
        title: 'Loan Originator',
        dept: 'Sales',
        color: '#1E6FA0',
        avatar: 'PR',
        email: 'priya@swifthl.com',
        phone: '(512) 555-0202',
        location: 'Austin, TX',
        recruitedBy: 'Carlos Mendez',
        children: []
      }]
    }, {
      id: 'mt',
      name: 'Marcus Thompson',
      title: 'Lead Processor',
      dept: 'Operations',
      color: '#1A2B3C',
      avatar: 'MT',
      email: 'marcus@swifthl.com',
      phone: '(214) 555-0301',
      location: 'Dallas, TX',
      recruitedBy: 'Robert Chen',
      children: [{
        id: 'ms',
        name: 'Maria Santos',
        title: 'Processor',
        dept: 'Operations',
        color: '#2B8DC4',
        avatar: 'MS',
        email: 'maria@swifthl.com',
        phone: '(214) 555-0303',
        location: 'Dallas, TX',
        recruitedBy: 'Marcus Thompson',
        children: []
      }, {
        id: 'dl',
        name: 'Derek Liu',
        title: 'Processor',
        dept: 'Operations',
        color: '#1E6FA0',
        avatar: 'DL',
        email: 'derek@swifthl.com',
        phone: '(888) 555-0302',
        location: 'Remote',
        recruitedBy: 'Marcus Thompson',
        children: []
      }]
    }, {
      id: 'jp',
      name: 'James Park',
      title: 'Senior Underwriter',
      dept: 'Underwriting',
      color: '#2B8DC4',
      avatar: 'JP',
      email: 'james@swifthl.com',
      phone: '(888) 555-0501',
      location: 'Remote',
      children: []
    }]
  }, {
    id: 'lm',
    name: 'Lisa Martinez',
    title: 'Training Manager',
    dept: 'HR & Training',
    color: '#1E6FA0',
    avatar: 'LM',
    email: 'lisa@swifthl.com',
    phone: '(602) 555-0401',
    location: 'Phoenix, AZ',
    children: []
  }, {
    id: 'tw',
    name: 'Tanya Williams',
    title: 'Marketing Specialist',
    dept: 'Marketing',
    color: '#1A2B3C',
    avatar: 'TW',
    email: 'tanya@swifthl.com',
    phone: '(602) 555-0601',
    location: 'Phoenix, AZ',
    children: []
  }, {
    id: 'dk',
    name: 'David Kim',
    title: 'IT Support Lead',
    dept: 'Technology',
    color: '#2B8DC4',
    avatar: 'DK',
    email: 'david@swifthl.com',
    phone: '(602) 555-0701',
    location: 'Phoenix, AZ',
    children: []
  }]
};
const deptColors: Record<string, string> = {
  Executive: '#1A2B3C',
  Sales: '#2B8DC4',
  Operations: '#1E6FA0',
  Underwriting: '#1A2B3C',
  'HR & Training': '#2B8DC4',
  Marketing: '#1E6FA0',
  Technology: '#1A2B3C'
};
function flattenTree(node: Person): Person[] {
  return [node, ...node.children.flatMap(flattenTree)];
}
const allPeople = flattenTree(orgTree);
// Find who someone recruited
function getRecruits(name: string): Person[] {
  return allPeople.filter(p => p.recruitedBy === name);
}
function buildReferralForest(people: Person[]): Person[] {
  const personMap = new Map<string, Person>();
  // First pass: clone everyone and clear children to build a new tree structure
  people.forEach(p => {
    personMap.set(p.name, {
      ...p,
      children: []
    });
  });
  const roots: Person[] = [];
  // Second pass: establish parent-child relationships based on recruitedBy
  people.forEach(p => {
    const clonedPerson = personMap.get(p.name)!;
    if (p.recruitedBy) {
      const recruiter = personMap.get(p.recruitedBy);
      if (recruiter) {
        recruiter.children.push(clonedPerson);
      } else {
        roots.push(clonedPerson);
      }
    } else {
      roots.push(clonedPerson);
    }
  });
  // Only return roots that actually recruited someone
  return roots.filter(r => r.children.length > 0);
}
const referralForest = buildReferralForest(allPeople);
function NodeCard({
  person,
  isMatch,
  isSelected,
  hasChildren,
  collapsed,
  onToggle,
  onSelect
}: any) {
  const deptColor = deptColors[person.dept] || '#2B8DC4';
  return <div onClick={() => onSelect(isSelected ? null : person)} style={{
    width: 164,
    background: '#FFFFFF',
    borderRadius: 12,
    padding: '14px 12px 10px',
    border: isMatch ? '2px solid #2B8DC4' : isSelected ? `2px solid ${person.color}` : '1px solid #E4EAF6',
    borderTop: isMatch ? '3px solid #2B8DC4' : isSelected ? `3px solid ${person.color}` : `3px solid ${person.color}`,
    boxShadow: isMatch ? '0 0 0 4px rgba(43,141,196,0.12), 0 2px 8px rgba(13,27,42,0.08)' : isSelected ? `0 0 0 4px ${person.color}20, 0 4px 16px rgba(13,27,42,0.12)` : '0 2px 8px rgba(13,27,42,0.06)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    flexShrink: 0
  }} onMouseEnter={e => {
    if (!isSelected && !isMatch) {
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(13,27,42,0.12)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }
  }} onMouseLeave={e => {
    if (!isSelected && !isMatch) {
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.06)';
      e.currentTarget.style.transform = 'translateY(0)';
    }
  }} data-id="element-182">
      <div style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 8
    }} data-id="element-183">
        <div style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        background: person.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 900,
        color: 'white',
        transform: 'skewX(-4deg)'
      }} data-id="element-184">
          {person.avatar}
        </div>
      </div>
      <div style={{
      fontSize: 13,
      fontWeight: 900,
      color: '#1A2B3C',
      textAlign: 'center',
      fontStyle: 'italic',
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
      marginBottom: 3
    }} data-id="element-185">
        {person.name}
      </div>
      <div style={{
      fontSize: 10,
      color: '#5A6A8A',
      textAlign: 'center',
      lineHeight: 1.3,
      marginBottom: 6,
      fontWeight: 500
    }} data-id="element-186">
        {person.title}
      </div>
      <div style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: hasChildren ? 8 : 0
    }} data-id="element-187">
        <span style={{
        fontSize: 9,
        fontWeight: 800,
        padding: '2px 7px',
        borderRadius: 4,
        background: '#EBF5FB',
        color: '#2B8DC4',
        letterSpacing: '0.04em',
        textTransform: 'uppercase'
      }} data-id="element-188">
          {person.dept}
        </span>
      </div>
      {hasChildren && <div style={{
      display: 'flex',
      justifyContent: 'center'
    }} data-id="element-189">
          <button onClick={e => {
        e.stopPropagation();
        onToggle();
      }} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: '3px 8px',
        borderRadius: 6,
        background: collapsed ? '#EBF5FB' : '#F4F8FC',
        border: `1px solid ${collapsed ? '#2B8DC4' : '#E4EAF6'}`,
        color: collapsed ? '#2B8DC4' : '#A0AECB',
        fontSize: 10,
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-190">
            {collapsed ? <ChevronDownIcon size={11} data-id="element-191" /> : <ChevronUpIcon size={11} data-id="element-192" />}
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>}
    </div>;
}
function OrgNode({
  person,
  depth,
  searchTerm,
  selectedId,
  onSelect
}: any) {
  const [collapsed, setCollapsed] = useState(depth >= 2);
  const hasChildren = person.children.length > 0;
  const isMatch = searchTerm.length > 0 && (person.name.toLowerCase().includes(searchTerm.toLowerCase()) || person.title.toLowerCase().includes(searchTerm.toLowerCase()) || person.dept.toLowerCase().includes(searchTerm.toLowerCase()));
  const isSelected = selectedId === person.id;
  const showChildren = hasChildren && !collapsed;
  const LINE_COLOR = '#C8D8E8';
  const LINE_WIDTH = 2;
  const V_GAP = 20;
  const H_PAD = 20;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }} data-id="element-193">
      <NodeCard person={person} isMatch={isMatch} isSelected={isSelected} hasChildren={hasChildren} collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} onSelect={onSelect} data-id="element-194" />
      {showChildren && <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }} data-id="element-195">
          <div style={{
        width: LINE_WIDTH,
        height: V_GAP,
        background: LINE_COLOR,
        flexShrink: 0
      }} data-id="element-196" />
          {person.children.length === 1 ? <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }} data-id="element-197">
              <OrgNode person={person.children[0]} depth={depth + 1} searchTerm={searchTerm} selectedId={selectedId} onSelect={onSelect} data-id="element-198" />
            </div> : <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }} data-id="element-199">
              <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          position: 'relative'
        }} data-id="element-200">
                {person.children.map((child: any, i: number) => {
            const isFirst = i === 0;
            const isLast = i === person.children.length - 1;
            return <div key={child.id} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingLeft: H_PAD,
              paddingRight: H_PAD,
              paddingTop: V_GAP,
              borderTop: `${LINE_WIDTH}px solid ${LINE_COLOR}`,
              borderLeft: isFirst ? `${LINE_WIDTH}px solid ${LINE_COLOR}` : 'none',
              borderRight: isLast ? `${LINE_WIDTH}px solid ${LINE_COLOR}` : 'none',
              borderTopLeftRadius: isFirst ? 8 : 0,
              borderTopRightRadius: isLast ? 8 : 0,
              marginLeft: i > 0 ? -LINE_WIDTH : 0
            }} data-id="element-201">
                      <OrgNode person={child} depth={depth + 1} searchTerm={searchTerm} selectedId={selectedId} onSelect={onSelect} data-id="element-202" />
                    </div>;
          })}
              </div>
            </div>}
        </div>}
    </div>;
}
function DetailPanel({
  person,
  onClose,
  treeMode
}: any) {
  const recruits = getRecruits(person.name);
  return <div style={{
    position: 'fixed',
    right: 24,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 280,
    background: '#FFFFFF',
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(13,27,42,0.18)',
    border: '1px solid #E4EAF6',
    zIndex: 100,
    overflow: 'hidden'
  }} data-id="element-203">
      <div style={{
      background: `linear-gradient(135deg, ${person.color} 0%, ${person.color}CC 100%)`,
      padding: '20px 16px 16px',
      position: 'relative'
    }} data-id="element-204">
        <button onClick={onClose} style={{
        position: 'absolute',
        top: 10,
        right: 10,
        background: 'rgba(255,255,255,0.2)',
        border: 'none',
        borderRadius: 6,
        color: 'white',
        cursor: 'pointer',
        padding: '2px 6px',
        fontSize: 14,
        fontWeight: 700
      }} data-id="element-205">
          ✕
        </button>
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8
      }} data-id="element-206">
          <div style={{
          width: 64,
          height: 64,
          borderRadius: 12,
          background: 'rgba(255,255,255,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 900,
          color: 'white',
          border: '2px solid rgba(255,255,255,0.4)',
          transform: 'skewX(-4deg)'
        }} data-id="element-207">
            {person.avatar}
          </div>
          <div data-id="element-208">
            <div style={{
            fontSize: 16,
            fontWeight: 900,
            color: 'white',
            textAlign: 'center',
            fontStyle: 'italic',
            letterSpacing: '-0.02em'
          }} data-id="element-209">
              {person.name}
            </div>
            <div style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.75)',
            textAlign: 'center',
            marginTop: 2
          }} data-id="element-210">
              {person.title}
            </div>
          </div>
          <span style={{
          fontSize: 10,
          fontWeight: 800,
          padding: '3px 10px',
          borderRadius: 5,
          background: 'rgba(255,255,255,0.2)',
          color: 'white',
          letterSpacing: '0.06em'
        }} data-id="element-211">
            {person.dept}
          </span>
        </div>
      </div>

      <div style={{
      padding: '16px'
    }} data-id="element-212">
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }} data-id="element-213">
          <a href={`mailto:${person.email}`} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none'
        }} data-id="element-214">
            <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: '#EBF5FB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-215">
              <MailIcon size={13} color="#2B8DC4" data-id="element-216" />
            </div>
            <span style={{
            fontSize: 12,
            color: '#2B8DC4',
            fontWeight: 600
          }} data-id="element-217">
              {person.email}
            </span>
          </a>
          <a href={`tel:${person.phone}`} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none'
        }} data-id="element-218">
            <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: '#EBF5FB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-219">
              <PhoneIcon size={13} color="#2B8DC4" data-id="element-220" />
            </div>
            <span style={{
            fontSize: 12,
            color: '#2B8DC4',
            fontWeight: 600
          }} data-id="element-221">
              {person.phone}
            </span>
          </a>
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }} data-id="element-222">
            <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: '#F4F8FC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-223">
              <MapPinIcon size={13} color="#5A6A8A" data-id="element-224" />
            </div>
            <span style={{
            fontSize: 12,
            color: '#5A6A8A',
            fontWeight: 500
          }} data-id="element-225">
              {person.location}
            </span>
          </div>
          {person.recruitedBy && <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }} data-id="element-226">
              <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: '#F4F8FC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-227">
                <UserPlusIcon size={13} color="#5A6A8A" data-id="element-228" />
              </div>
              <span style={{
            fontSize: 12,
            color: '#5A6A8A',
            fontWeight: 500
          }} data-id="element-229">
                Recruited by:{' '}
                <span style={{
              color: '#1A2B3C',
              fontWeight: 700
            }} data-id="element-230">
                  {person.recruitedBy}
                </span>
              </span>
            </div>}
        </div>

        {treeMode === 'referral' && recruits.length > 0 && <div style={{
        marginTop: 16,
        paddingTop: 16,
        borderTop: '1px solid #E4EAF6'
      }} data-id="element-231">
            <div style={{
          fontSize: 10,
          fontWeight: 800,
          color: '#A0AECB',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 10
        }} data-id="element-232">
              Recruited Family Tree ({recruits.length})
            </div>
            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }} data-id="element-233">
              {recruits.map((child: any) => <div key={child.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px',
            borderRadius: 8,
            background: '#F4F8FC'
          }} data-id="element-234">
                  <div style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: child.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 800,
              color: 'white',
              flexShrink: 0,
              transform: 'skewX(-4deg)'
            }} data-id="element-235">
                    {child.avatar}
                  </div>
                  <div data-id="element-236">
                    <div style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#1A2B3C'
              }} data-id="element-237">
                      {child.name}
                    </div>
                    <div style={{
                fontSize: 10,
                color: '#5A6A8A'
              }} data-id="element-238">
                      {child.title}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}

        {treeMode === 'hierarchy' && person.children.length > 0 && <div style={{
        marginTop: 16,
        paddingTop: 16,
        borderTop: '1px solid #E4EAF6'
      }} data-id="element-239">
            <div style={{
          fontSize: 10,
          fontWeight: 800,
          color: '#A0AECB',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 10
        }} data-id="element-240">
              Direct Reports ({person.children.length})
            </div>
            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }} data-id="element-241">
              {person.children.map((child: any) => <div key={child.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }} data-id="element-242">
                  <div style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: child.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 800,
              color: 'white',
              flexShrink: 0,
              transform: 'skewX(-4deg)'
            }} data-id="element-243">
                    {child.avatar}
                  </div>
                  <div data-id="element-244">
                    <div style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#1A2B3C'
              }} data-id="element-245">
                      {child.name}
                    </div>
                    <div style={{
                fontSize: 10,
                color: '#5A6A8A'
              }} data-id="element-246">
                      {child.title}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}
function GridView({
  people,
  searchTerm
}: any) {
  const filtered = searchTerm ? people.filter((p: any) => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.title.toLowerCase().includes(searchTerm.toLowerCase()) || p.dept.toLowerCase().includes(searchTerm.toLowerCase())) : people;
  return <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16
  }} data-id="element-247">
      {filtered.map((person: any) => <div key={person.id} style={{
      background: '#FFFFFF',
      borderRadius: 12,
      padding: '20px 16px',
      border: '1px solid #E4EAF6',
      borderTop: `3px solid ${person.color}`,
      boxShadow: '0 2px 8px rgba(13,27,42,0.06)',
      transition: 'all 0.15s'
    }} onMouseEnter={e => {
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(13,27,42,0.12)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }} onMouseLeave={e => {
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.06)';
      e.currentTarget.style.transform = 'translateY(0)';
    }} data-id="element-248">
          <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12
      }} data-id="element-249">
            <div style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: person.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 900,
          color: 'white',
          transform: 'skewX(-4deg)',
          flexShrink: 0
        }} data-id="element-250">
              {person.avatar}
            </div>
            <div data-id="element-251">
              <div style={{
            fontSize: 14,
            fontWeight: 900,
            color: '#1A2B3C',
            fontStyle: 'italic',
            letterSpacing: '-0.02em',
            lineHeight: 1.2
          }} data-id="element-252">
                {person.name}
              </div>
              <div style={{
            fontSize: 11,
            color: '#5A6A8A',
            marginTop: 2
          }} data-id="element-253">
                {person.title}
              </div>
            </div>
          </div>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6
      }} data-id="element-254">
            <span style={{
          fontSize: 10,
          fontWeight: 800,
          padding: '2px 7px',
          borderRadius: 4,
          background: '#EBF5FB',
          color: '#2B8DC4',
          display: 'inline-block',
          width: 'fit-content'
        }} data-id="element-255">
              {person.dept}
            </span>
            <div style={{
          fontSize: 11,
          color: '#A0AECB',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-256">
              <MapPinIcon size={11} data-id="element-257" /> {person.location}
            </div>
            {person.recruitedBy && <div style={{
          fontSize: 11,
          color: '#A0AECB',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-258">
                <UserPlusIcon size={11} data-id="element-259" /> Recruited by {person.recruitedBy}
              </div>}
          </div>
        </div>)}
    </div>;
}
export function Directory() {
  const [zoom, setZoom] = useState(0.85);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'tree' | 'grid'>('tree');
  const [treeMode, setTreeMode] = useState<'hierarchy' | 'referral'>('hierarchy');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const deptCounts = allPeople.reduce<Record<string, number>>((acc, p) => {
    acc[p.dept] = (acc[p.dept] || 0) + 1;
    return acc;
  }, {});
  return <div style={{
    maxWidth: '100%',
    margin: '0 auto'
  }} data-id="element-260">
      <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 20,
      flexWrap: 'wrap',
      gap: 12
    }} data-id="element-261">
        <div data-id="element-262">
          <h2 style={{
          fontSize: 26,
          fontWeight: 900,
          color: '#1A2B3C',
          margin: '0 0 3px',
          fontStyle: 'italic',
          letterSpacing: '-0.04em'
        }} data-id="element-263">
            Company Directory
          </h2>
          <p style={{
          fontSize: 13,
          color: '#5A6A8A',
          margin: 0
        }} data-id="element-264">
            {allPeople.length} team members · {Object.keys(deptCounts).length}{' '}
            departments
          </p>
        </div>

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10
      }} data-id="element-265">
          <div style={{
          position: 'relative'
        }} data-id="element-266">
            <SearchIcon size={14} style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#A0AECB'
          }} data-id="element-267" />
            <input type="text" placeholder="Search name, title, dept..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            paddingLeft: 30,
            paddingRight: 12,
            paddingTop: 8,
            paddingBottom: 8,
            fontSize: 13,
            borderRadius: 8,
            border: '1.5px solid #E4EAF6',
            background: '#F4F8FC',
            color: '#1A2B3C',
            outline: 'none',
            width: 220
          }} onFocus={e => {
            e.target.style.borderColor = '#2B8DC4';
          }} onBlur={e => {
            e.target.style.borderColor = '#E4EAF6';
          }} data-id="element-268" />
          </div>

          <div style={{
          display: 'flex',
          background: '#FFFFFF',
          borderRadius: 8,
          border: '1px solid #E4EAF6',
          overflow: 'hidden'
        }} data-id="element-269">
            <button onClick={() => setView('tree')} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '7px 12px',
            background: view === 'tree' ? '#2B8DC4' : 'transparent',
            color: view === 'tree' ? 'white' : '#5A6A8A',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            transition: 'all 0.15s'
          }} data-id="element-270">
              <GitBranchIcon size={13} data-id="element-271" /> Tree
            </button>
            <button onClick={() => setView('grid')} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '7px 12px',
            background: view === 'grid' ? '#2B8DC4' : 'transparent',
            color: view === 'grid' ? 'white' : '#5A6A8A',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            transition: 'all 0.15s'
          }} data-id="element-272">
              <LayoutGridIcon size={13} data-id="element-273" /> Grid
            </button>
          </div>

          {view === 'tree' && <>
              <div style={{
            display: 'flex',
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E4EAF6',
            overflow: 'hidden'
          }} data-id="element-274">
                <button onClick={() => setTreeMode('hierarchy')} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '7px 12px',
              background: treeMode === 'hierarchy' ? '#1A2B3C' : 'transparent',
              color: treeMode === 'hierarchy' ? 'white' : '#5A6A8A',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              transition: 'all 0.15s'
            }} data-id="element-275">
                  <UsersIcon size={13} data-id="element-276" /> Hierarchy
                </button>
                <button onClick={() => setTreeMode('referral')} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '7px 12px',
              background: treeMode === 'referral' ? '#2B8DC4' : 'transparent',
              color: treeMode === 'referral' ? 'white' : '#5A6A8A',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              transition: 'all 0.15s'
            }} data-id="element-277">
                  <UserPlusIcon size={13} data-id="element-278" /> Referral Tree
                </button>
              </div>

              <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #E4EAF6',
            padding: '4px 6px'
          }} data-id="element-279">
                <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '3px 5px',
              borderRadius: 5,
              color: '#5A6A8A',
              display: 'flex',
              alignItems: 'center'
            }} title="Zoom out" data-id="element-280">
                  <ZoomOutIcon size={15} data-id="element-281" />
                </button>
                <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#5A6A8A',
              minWidth: 36,
              textAlign: 'center'
            }} data-id="element-282">
                  {Math.round(zoom * 100)}%
                </span>
                <button onClick={() => setZoom(z => Math.min(1.4, z + 0.1))} style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '3px 5px',
              borderRadius: 5,
              color: '#5A6A8A',
              display: 'flex',
              alignItems: 'center'
            }} title="Zoom in" data-id="element-283">
                  <ZoomInIcon size={15} data-id="element-284" />
                </button>
                <button onClick={() => setZoom(0.85)} style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '3px 5px',
              borderRadius: 5,
              color: '#5A6A8A',
              display: 'flex',
              alignItems: 'center'
            }} title="Reset zoom" data-id="element-285">
                  <RotateCcwIcon size={13} data-id="element-286" />
                </button>
              </div>
            </>}
        </div>
      </div>

      {view === 'grid' ? <GridView people={allPeople} searchTerm={searchTerm} data-id="element-287" /> : <div style={{
      background: '#FFFFFF',
      borderRadius: 12,
      border: '1px solid #E4EAF6',
      boxShadow: '0 2px 8px rgba(13,27,42,0.06)',
      overflow: 'auto',
      padding: '40px 24px',
      minHeight: 400
    }} data-id="element-288">
          <div style={{
        position: 'relative',
        backgroundImage: 'radial-gradient(circle, #C8D8E8 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        borderRadius: 12,
        padding: '32px 16px'
      }} data-id="element-289">
            <div style={{
          transform: `scale(${zoom})`,
          transformOrigin: 'top center',
          transition: 'transform 0.2s ease',
          display: 'flex',
          justifyContent: 'center'
        }} data-id="element-290">
              {treeMode === 'hierarchy' ? <OrgNode person={orgTree} depth={0} searchTerm={searchTerm} selectedId={selectedPerson?.id || null} onSelect={setSelectedPerson} data-id="element-291" /> : <div style={{
            display: 'flex',
            gap: '80px',
            alignItems: 'flex-start'
          }} data-id="element-292">
                  {referralForest.map(root => <OrgNode key={root.id} person={root} depth={0} searchTerm={searchTerm} selectedId={selectedPerson?.id || null} onSelect={setSelectedPerson} data-id="element-293" />)}
                </div>}
            </div>
          </div>
          <div style={{
        textAlign: 'center',
        marginTop: 16,
        fontSize: 11,
        color: '#A0AECB'
      }} data-id="element-294">
            {treeMode === 'hierarchy' ? 'Click any card to view details · Use Show/Hide to collapse branches · Scroll to explore' : 'Showing recruitment relationships · Click any card to view details · Scroll to explore'}
          </div>
        </div>}

      {selectedPerson && <DetailPanel person={selectedPerson} onClose={() => setSelectedPerson(null)} treeMode={treeMode} data-id="element-295" />}
    </div>;
}